// Dynamic favicon engine for MA-OS.
// Uses the real brand favicon as the base image and overlays live state badges.

export type FaviconState =
  | { type: 'idle' }
  | { type: 'healthy' }
  | { type: 'alert'; count: number }
  | { type: 'overdue'; count: number }
  | { type: 'pulse' };

// Brand colors
const C_BG_OUT  = '#020617';
const C_ALERT   = '#ef4444';
const C_OVERDUE = '#f59e0b';
const C_PULSE   = '#06b6d4';
const STATIC_FAVICON_PNG = '/meos/images/favicon32.png?v=20260525d';

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
}

function drawBadge(ctx: CanvasRenderingContext2D, count: number, color: string) {
  const label = count > 99 ? '99+' : String(count);
  const wide = count > 9;

  // Dark halo so badge pops on any background
  ctx.fillStyle = C_BG_OUT;
  ctx.beginPath();
  if (wide) {
    roundedRect(ctx, 18.5, -0.5, 14, 12, 6);
  } else {
    ctx.arc(27, 5, 6.5, 0, Math.PI * 2);
  }
  ctx.fill();

  // Badge fill
  ctx.fillStyle = color;
  ctx.beginPath();
  if (wide) {
    roundedRect(ctx, 19, 0, 13, 11, 5.5);
  } else {
    ctx.arc(27, 5, 5.5, 0, Math.PI * 2);
  }
  ctx.fill();

  // Badge text
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${wide ? 5.5 : 7}px -apple-system, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, wide ? 25.5 : 27, wide ? 5.5 : 5);
}

function drawStatusDot(ctx: CanvasRenderingContext2D, color: string, alpha = 1) {
  // Bottom-right corner dot with dark halo
  ctx.fillStyle = C_BG_OUT;
  ctx.beginPath();
  ctx.arc(26.5, 26.5, 5, 0, Math.PI * 2);
  ctx.fill();

  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(26.5, 26.5, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

function flushToLink(canvas: HTMLCanvasElement) {
  const dataUrl = canvas.toDataURL('image/png');
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"][data-maos-dynamic]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.setAttribute('data-maos-dynamic', '1');
    document.head.appendChild(link);
  }
  link.href = dataUrl;
}

function clearDynamicLink() {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"][data-maos-dynamic]');
  if (link?.parentNode) link.parentNode.removeChild(link);
}

function restoreStaticFavicon() {
  clearDynamicLink();
}

class FaviconControllerClass {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private state: FaviconState = { type: 'idle' };
  private animId: number | null = null;
  private baseImage: HTMLImageElement | null = null;
  private baseImageReady = false;
  private ready = false;

  init() {
    if (this.ready || typeof document === 'undefined') return;
    this.canvas = document.createElement('canvas');
    this.canvas.width = 32;
    this.canvas.height = 32;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    this.ctx = ctx;
    this.baseImage = new Image();
    this.baseImage.onload = () => {
      this.baseImageReady = true;
      if (this.state.type === 'alert' || this.state.type === 'overdue' || this.state.type === 'pulse') {
        this.render();
      } else {
        restoreStaticFavicon();
      }
    };
    this.baseImage.onerror = () => {
      this.baseImageReady = false;
      restoreStaticFavicon();
    };
    this.baseImage.src = STATIC_FAVICON_PNG;
    this.ready = true;
    restoreStaticFavicon();
  }

  setState(next: FaviconState) {
    if (!this.ready) this.init();
    this.state = next;
    this.stopAnimation();

    if (next.type === 'idle' || next.type === 'healthy') {
      restoreStaticFavicon();
      return;
    }

    if (next.type === 'pulse') {
      this.startPulse();
    } else {
      this.render();
    }
  }

  getState(): FaviconState {
    return this.state;
  }

  private render(frame = 0) {
    const ctx = this.ctx;
    const canvas = this.canvas;
    if (!ctx || !canvas) return;
    if (!this.baseImageReady || !this.baseImage) {
      restoreStaticFavicon();
      return;
    }

    ctx.clearRect(0, 0, 32, 32);
    ctx.drawImage(this.baseImage, 0, 0, 32, 32);

    const s = this.state;
    if (s.type === 'alert' && s.count > 0) {
      drawBadge(ctx, s.count, C_ALERT);
    } else if (s.type === 'overdue' && s.count > 0) {
      drawBadge(ctx, s.count, C_OVERDUE);
    } else if (s.type === 'pulse') {
      const alpha = 0.6 + Math.sin(frame * 0.05) * 0.4;
      drawStatusDot(ctx, C_PULSE, alpha);
    }

    flushToLink(canvas);
  }

  private startPulse() {
    let frame = 0;
    const tick = () => {
      this.render(frame++);
      this.animId = requestAnimationFrame(tick);
    };
    this.animId = requestAnimationFrame(tick);
  }

  private stopAnimation() {
    if (this.animId !== null) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
  }
}

export const FaviconController = new FaviconControllerClass();
